import { useState, useEffect, useCallback, useRef } from 'react';
import { DEFAULT_BACKGROUND } from '@/components/ChildCardBackgrounds';
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

// Hook to manage background preferences for a parent
// Preferences are stored in the app_users.preferences JSONB column
// updatePreferences: optional lightweight function from AuthContext to update preferences without full refresh
export function useChildBackgrounds(userId, userPreferences, updatePreferences = null) {
  const [backgrounds, setBackgrounds] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Keep a local ref of all preferences to avoid stale closure issues
  // This gets updated from props AND after successful saves
  const preferencesRef = useRef(userPreferences || {});
  
  // Track the last saved state per child - this is what we know is in the backend
  const lastSavedBackgroundsRef = useRef({});
  
  // Track if we've initialized to avoid overwriting local changes
  const isInitializedRef = useRef(false);

  // Load backgrounds from user preferences (passed from AuthContext)
  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    // Update our local ref with latest preferences from props
    preferencesRef.current = userPreferences || {};

    // Get child_backgrounds from preferences object
    const childBackgrounds = userPreferences?.child_backgrounds || {};
    
    if (!isInitializedRef.current) {
      // Initial load: use props and remember them as the last saved state
      setBackgrounds(childBackgrounds);
      lastSavedBackgroundsRef.current = { ...childBackgrounds };
      isInitializedRef.current = true;
    } else {
      // After initialization: only update from props if they match what we last saved
      // This prevents stale props from overwriting local changes
      setBackgrounds(prev => {
        const merged = { ...prev };
        
        // For each child in props, only update if:
        // 1. We don't have it locally, OR
        // 2. The props value matches what we last saved (meaning props are fresh)
        Object.keys(childBackgrounds).forEach(childId => {
          const propsValue = childBackgrounds[childId];
          const lastSavedValue = lastSavedBackgroundsRef.current[childId];
          const localValue = prev[childId];
          
          if (localValue === undefined) {
            // New child we don't have locally - add it
            merged[childId] = propsValue;
            lastSavedBackgroundsRef.current[childId] = propsValue;
          } else if (propsValue === lastSavedValue) {
            // Props match what we last saved - props are fresh, use them
            merged[childId] = propsValue;
          }
          // Otherwise: props are stale (different from what we saved), keep local value
        });
        
        return merged;
      });
    }
    
    setIsLoading(false);
  }, [userId, userPreferences]);

  // Get background for a specific child
  const getBackground = useCallback((childId) => {
    return backgrounds[childId] || DEFAULT_BACKGROUND;
  }, [backgrounds]);

  // Set background for a specific child
  const setBackground = useCallback(async (childId, presetKey) => {
    if (!userId || !childId) return;

    // Optimistically update local state
    const newBackgrounds = {
      ...backgrounds,
      [childId]: presetKey,
    };
    setBackgrounds(newBackgrounds);

    // Save to backend - use preferencesRef.current to avoid stale closure
    try {
      const newPreferences = {
        ...preferencesRef.current,
        child_backgrounds: newBackgrounds,
      };

      // Save to backend using direct supabase call (avoids full profile refresh)
      const { error } = await supabase
        .from('app_users')
        .update({ preferences: newPreferences })
        .eq('id', userId);

      if (error) {
        logger.error('Failed to save background preference:', error);
        // Revert on error
        setBackgrounds(backgrounds);
      } else {
        // Update our local ref after successful save
        preferencesRef.current = newPreferences;
        // Update last saved state - this is now what's in the backend
        lastSavedBackgroundsRef.current = { ...newBackgrounds };
        
        // Lightweight context update (no full refresh)
        if (updatePreferences) {
          updatePreferences(newPreferences);
        }
      }
    } catch (err) {
      logger.error('Error saving background preference:', err);
      // Revert on error
      setBackgrounds(backgrounds);
    }
  }, [userId, backgrounds, updatePreferences]);

  return {
    backgrounds,
    getBackground,
    setBackground,
    isLoading,
  };
}
